import React from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { createStyles } from './PaywallScreen.styles';
import { useTranslation } from '@/src/i18n';

interface ComparisonItem {
  feature: string;
  free: string;
  pro: string;
}

interface PaywallScreenProps {
  colors: any;
  isPro: boolean;
  selectedPlan: 'monthly' | 'yearly';
  onSelectPlan: (plan: 'monthly' | 'yearly') => void;
  onSubscribe: () => void;
  onRestore: () => void;
  loading: boolean;
  periodEnd?: string;
  monthlyPrice?: string;
  yearlyPrice?: string;
}

export function PaywallScreen({
  colors,
  isPro,
  selectedPlan,
  onSelectPlan,
  onSubscribe,
  onRestore,
  loading,
  periodEnd,
  monthlyPrice,
  yearlyPrice,
}: PaywallScreenProps) {
  const styles = createStyles();
  const { t } = useTranslation();

  const proFeatures = [
    t('subscription.proFeatures.unlimitedTournaments'),
    t('subscription.proFeatures.unlimitedParticipants'),
    t('subscription.proFeatures.allFormats'),
    t('subscription.proFeatures.unlimitedLeagues'),
    t('subscription.proFeatures.pdfExport'),
    t('subscription.proFeatures.noWatermark'),
    t('subscription.proFeatures.noAds'),
    t('subscription.proFeatures.proBadge'),
  ];

  const comparison: ComparisonItem[] = [
    { feature: t('subscription.comparison.activeTournaments'), free: '3', pro: t('subscription.unlimited') },
    { feature: t('subscription.comparison.participants'), free: '16', pro: '512' },
    { feature: t('subscription.comparison.tournamentTypes'), free: 'SE, RR', pro: t('subscription.comparison.allTypes') },
    { feature: t('subscription.comparison.activeLeagues'), free: '1', pro: t('subscription.unlimited') },
    { feature: t('subscription.comparison.privateTournaments'), free: '1', pro: t('subscription.unlimited') },
    { feature: t('subscription.comparison.pdfExport'), free: '-', pro: '\u2713' },
    { feature: t('subscription.comparison.watermark'), free: t('common.yes'), pro: t('common.no') },
    { feature: t('subscription.comparison.ads'), free: t('common.yes'), pro: t('common.no') },
  ];

  if (isPro) {
    return (
      <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.alreadyProSection}>
          <Ionicons name="star" size={64} color="#FFD700" />
          <Text style={[styles.alreadyProTitle, { color: colors.text }]}>
            {t('subscription.youArePro')}
          </Text>
          <Text style={[styles.alreadyProSubtitle, { color: colors.textSecondary }]}>
            {t('subscription.proDescription')}
          </Text>
          {periodEnd && (
            <View style={[styles.periodInfo, { backgroundColor: colors.surface }]}>
              <Text style={[styles.periodInfoText, { color: colors.textSecondary }]}>
                {t('subscription.renewsOn', { date: periodEnd })}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <Ionicons name="star" size={48} color="#FFD700" style={styles.proIcon} />
          <Text style={[styles.title, { color: colors.text }]}>Tournaly Pro</Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('subscription.paywallSubtitle')}
          </Text>
        </View>

        {/* Pro Features */}
        <View style={styles.featuresSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {t('subscription.whatsIncluded')}
          </Text>
          {proFeatures.map((feature, index) => (
            <View key={index} style={styles.featureRow}>
              <Ionicons name="checkmark-circle" size={24} color={colors.success || '#34C759'} style={styles.featureIcon} />
              <Text style={[styles.featureText, { color: colors.text }]}>{feature}</Text>
            </View>
          ))}
        </View>

        {/* Comparison Table */}
        <View style={styles.comparisonSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {t('subscription.comparison.title')}
          </Text>
          <View style={[styles.comparisonHeader, { borderBottomColor: colors.border }]}>
            <Text style={[styles.comparisonFeatureHeader, { color: colors.text }]}>
              {t('subscription.comparison.feature')}
            </Text>
            <Text style={[styles.comparisonPlanHeader, { color: colors.textSecondary }]}>
              {t('subscription.free')}
            </Text>
            <Text style={[styles.comparisonPlanHeader, { color: '#FFD700' }]}>
              {t('subscription.pro')}
            </Text>
          </View>
          {comparison.map((item, index) => (
            <View key={index} style={[styles.comparisonRow, { borderBottomColor: colors.divider }]}>
              <Text style={[styles.comparisonFeature, { color: colors.text }]}>{item.feature}</Text>
              <Text style={[styles.comparisonValue, { color: colors.textSecondary }]}>{item.free}</Text>
              <Text style={[styles.comparisonValue, { color: colors.primary }]}>{item.pro}</Text>
            </View>
          ))}
        </View>

        {/* Pricing */}
        <View style={styles.pricingSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {t('subscription.choosePlan')}
          </Text>

          {/* Yearly Plan */}
          <TouchableOpacity
            style={[
              styles.planCard,
              {
                borderColor: selectedPlan === 'yearly' ? colors.primary : colors.border,
                backgroundColor: selectedPlan === 'yearly' ? colors.surface : colors.background,
              },
            ]}
            onPress={() => onSelectPlan('yearly')}
          >
            <View style={styles.planCardHeader}>
              <Text style={[styles.planName, { color: colors.text }]}>{t('subscription.yearly')}</Text>
              <View style={[styles.savingsBadge, { backgroundColor: colors.success || '#34C759' }]}>
                <Text style={[styles.savingsText, { color: '#fff' }]}>{t('subscription.yearlySavings')}</Text>
              </View>
            </View>
            <Text style={[styles.planPrice, { color: colors.text }]}>{yearlyPrice ?? t('subscription.yearlyPrice')}</Text>
          </TouchableOpacity>

          {/* Monthly Plan */}
          <TouchableOpacity
            style={[
              styles.planCard,
              {
                borderColor: selectedPlan === 'monthly' ? colors.primary : colors.border,
                backgroundColor: selectedPlan === 'monthly' ? colors.surface : colors.background,
              },
            ]}
            onPress={() => onSelectPlan('monthly')}
          >
            <Text style={[styles.planName, { color: colors.text }]}>{t('subscription.monthly')}</Text>
            <Text style={[styles.planPrice, { color: colors.text }]}>{monthlyPrice ?? t('subscription.monthlyPrice')}</Text>
          </TouchableOpacity>
        </View>

        {/* Subscribe Button */}
        <TouchableOpacity
          style={[styles.subscribeButton, { backgroundColor: colors.primary, opacity: loading ? 0.6 : 1 }]}
          onPress={onSubscribe}
          disabled={loading}
        >
          <Text style={styles.subscribeButtonText}>
            {loading ? t('common.loading') : t('subscription.subscribe')}
          </Text>
        </TouchableOpacity>

        {/* Restore Purchases */}
        <TouchableOpacity style={styles.restoreButton} onPress={onRestore} disabled={loading}>
          <Text style={[styles.restoreButtonText, { color: colors.primary }]}>
            {t('subscription.restorePurchases')}
          </Text>
        </TouchableOpacity>

        {/* Legal Text */}
        <Text style={[styles.legalText, { color: colors.textTertiary }]}>
          {t('subscription.legalText')}
        </Text>
      </View>
    </ScrollView>
  );
}
